import { useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ListPlus,
  Plus,
  Power,
  PowerOff,
  RefreshCcw,
  Search as SearchIcon,
  Users,
  Workflow,
} from "lucide-react";

import { JourneyBuilderModal } from "@/components/admin/marketing/journeys/journey-builder-modal";
import { JourneyDetailDrawer } from "@/components/admin/marketing/journeys/journey-detail-drawer";
import {
  journeyErrorNamesSteps,
  journeyErrorText,
} from "@/components/admin/marketing/journeys/journey-errors";
import {
  JOURNEYS_LIST_QUERY_KEY,
  JOURNEYS_QUERY_PREFIX,
  journeyDetailQueryKey,
} from "@/components/admin/marketing/journeys/journey-queries";
import {
  journeyTriggerLabel,
  useJourneyTriggers,
} from "@/components/admin/marketing/journeys/use-journey-triggers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Marketing → Journey automation.
 *
 * V1.1 P13 port of `src/app/admin/marketing/journeys/page.tsx` (~218 LOC).
 *
 * Surfaces:
 *   - List table + filters (trigger event, status, search)
 *   - KPI strip (total / active / enrollments / steps)
 *   - "Ensure default journeys" — creates the missing defaults (inactive),
 *     moves legacy ones onto the real trigger, rewrites untouched legacy seed
 *     copy as [DRAFT] briefs; never overwrites an edited journey
 *   - Builder modal trigger + edit
 *   - Detail drawer with enrollment audit and per-step copy editing
 *
 * Endpoints:
 *   GET    /api/admin/marketing/journeys
 *   GET    /api/admin/marketing/journeys/triggers         (trigger labels + filter)
 *   POST   /api/admin/marketing/journeys/ensure-defaults  → { created, migrated, neutralized }
 *   PATCH  /api/admin/marketing/journeys/:id              ({ isActive } — Pause / Resume)
 *
 * The list is a `useQuery` keyed on `dbTarget` (Local↔Prod admin toggle).
 * Pause / Resume and Ensure defaults show saving → saved / error toasts; a
 * refusal (e.g. 422 draft_steps on Resume) is read by `journeyErrorText`.
 */

export const Route = createFileRoute("/_authenticated/marketing-journeys")({
  component: MarketingJourneysPage,
});

interface JourneyListItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  triggerEvent: string;
  isActive: boolean;
  createdAt: string;
  _count: { steps: number; enrollments: number };
}

interface EnsureDefaultsResponse {
  created?: unknown;
  migrated?: unknown;
  neutralized?: unknown;
}

type StatusFilter = "" | "active" | "inactive";

const countOf = (value: unknown) => (Array.isArray(value) ? value.length : 0);

function MarketingJourneysPage() {
  const { t, i18n } = useLingui();
  const { dbTarget } = useAdminContext();
  const queryClient = useQueryClient();
  const catalog = useJourneyTriggers().data;

  const [trigger, setTrigger] = useState("");
  const [status, setStatus] = useState<StatusFilter>("");
  const [search, setSearch] = useState("");

  const [showBuilder, setShowBuilder] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [ensuring, setEnsuring] = useState(false);

  // Load journeys ------------------------------------------------------------
  const listKey = [...JOURNEYS_LIST_QUERY_KEY, dbTarget] as const;
  const journeysQuery = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const rows = await apiFetch.get<JourneyListItem[]>("/api/admin/marketing/journeys");
      return Array.isArray(rows) ? rows : [];
    },
  });
  const journeys = useMemo(() => journeysQuery.data ?? [], [journeysQuery.data]);
  const loading = journeysQuery.isPending;
  const error = journeysQuery.isError
    ? journeyErrorText(journeysQuery.error, i18n, { fallback: t`Could not load the journeys` })
    : null;

  /** Reload this list and mark the journey picker on Marketing → Settings stale. */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: JOURNEYS_QUERY_PREFIX });
  };

  const knownTrigger = (event: string) =>
    !catalog || catalog.triggers.some((tr) => tr.event === event);

  // Filter -------------------------------------------------------------------
  /** The API's triggers, then any trigger a journey holds that is not one of them. */
  const triggerOptions = useMemo(() => {
    const events = (catalog?.triggers ?? []).map((tr) => tr.event);
    for (const j of journeys) {
      if (!events.includes(j.triggerEvent)) events.push(j.triggerEvent);
    }
    return events;
  }, [catalog, journeys]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return journeys.filter((j) => {
      if (trigger && j.triggerEvent !== trigger) return false;
      if (status === "active" && !j.isActive) return false;
      if (status === "inactive" && j.isActive) return false;
      if (q) {
        const hay = `${j.code} ${j.name} ${j.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [journeys, trigger, status, search]);

  const stats = useMemo(() => {
    let active = 0;
    let enrollments = 0;
    let steps = 0;
    for (const j of journeys) {
      if (j.isActive) active++;
      enrollments += j._count.enrollments;
      steps += j._count.steps;
    }
    return { total: journeys.length, active, enrollments, steps };
  }, [journeys]);

  // Pause / Resume -----------------------------------------------------------
  const toggleActive = async (j: JourneyListItem) => {
    const next = !j.isActive;
    setTogglingId(j.id);
    const toast = showToast(next ? t`Resuming the journey…` : t`Pausing the journey…`, "loading");
    try {
      await apiFetch.patch(`/api/admin/marketing/journeys/${j.id}`, { isActive: next });
      queryClient.setQueryData<JourneyListItem[]>(listKey, (prev) =>
        prev?.map((x) => (x.id === j.id ? { ...x, isActive: next } : x)),
      );
      void queryClient.invalidateQueries({ queryKey: journeyDetailQueryKey(j.id) });
      toast.update(
        next ? t`Journey resumed` : t`Journey paused — its enrollments wait where they are`,
        "success",
      );
    } catch (e) {
      // The refusal names steps by stepOrder; load them to say "#n" as the
      // drawer shows it. Best effort — without them the text stays generic.
      const stepOrders = journeyErrorNamesSteps(e)
        ? await apiFetch
            .get<{ steps?: Array<{ stepOrder?: unknown }> }>(
              `/api/admin/marketing/journeys/${j.id}`,
            )
            .then((d) =>
              (d.steps ?? [])
                .map((s) => s.stepOrder)
                .filter((o): o is number => typeof o === "number"),
            )
            .catch(() => undefined)
        : undefined;
      toast.update(
        journeyErrorText(e, i18n, {
          fallback: next ? t`Could not resume the journey` : t`Could not pause the journey`,
          stepOrders,
        }),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  // Ensure default journeys --------------------------------------------------
  const ensureDefaults = async () => {
    setEnsuring(true);
    const toast = showToast(t`Checking the default journeys…`, "loading");
    try {
      const res = await apiFetch.post<EnsureDefaultsResponse | undefined>(
        "/api/admin/marketing/journeys/ensure-defaults",
      );
      const created = countOf(res?.created);
      const migrated = countOf(res?.migrated);
      const rewritten = countOf(res?.neutralized);
      toast.update(
        created + migrated + rewritten === 0
          ? t`The default journeys are all in place — nothing changed.`
          : t`Default journeys: ${created} created (inactive), ${migrated} moved to the real trigger, ${rewritten} legacy steps rewritten as [DRAFT] briefs.`,
        "success",
      );
      refresh();
    } catch (e) {
      toast.update(
        journeyErrorText(e, i18n, { fallback: t`Could not check the default journeys` }),
        "error",
      );
    } finally {
      setEnsuring(false);
    }
  };

  // Builder/detail handlers --------------------------------------------------
  const openCreate = () => {
    setEditId(null);
    setShowBuilder(true);
  };
  const openEdit = (id: string) => {
    setOpenDetailId(null);
    setEditId(id);
    setShowBuilder(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            <Trans>Journeys</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              Multi-step lifecycle automation — welcome, nurture, win-back. Triggered by event.
            </Trans>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={journeysQuery.isFetching}
            className="min-h-11"
          >
            <RefreshCcw className="size-4" />
            <Trans>Refresh</Trans>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void ensureDefaults()}
            disabled={ensuring}
            className="min-h-11"
          >
            <ListPlus className="size-4" />
            <Trans>Ensure default journeys</Trans>
          </Button>
          <Button size="sm" onClick={openCreate} className="min-h-11">
            <Plus className="size-4" />
            <Trans>New journey</Trans>
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-3">
            <Workflow className="text-muted-foreground size-5" />
            <div>
              <div className="text-xl font-bold">{loading ? "—" : stats.total}</div>
              <div className="text-muted-foreground text-xs">
                <Trans>Total</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-3">
            <Power className="text-success size-5" />
            <div>
              <div className="text-xl font-bold">{loading ? "—" : stats.active}</div>
              <div className="text-muted-foreground text-xs">
                <Trans>Active</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-3">
            <Users className="text-primary size-5" />
            <div>
              <div className="text-xl font-bold">{loading ? "—" : stats.enrollments}</div>
              <div className="text-muted-foreground text-xs">
                <Trans>Enrollments</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-3">
            <Workflow className="text-muted-foreground size-5" />
            <div>
              <div className="text-xl font-bold">{loading ? "—" : stats.steps}</div>
              <div className="text-muted-foreground text-xs">
                <Trans>Steps</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-3">
          <div className="min-w-[14rem] flex-1">
            <label htmlFor="journeys-search" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Search</Trans>
            </label>
            <div className="relative">
              <SearchIcon className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <input
                id="journeys-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t`Code, name, description…`}
                className="border-border bg-background min-h-11 w-full rounded-md border py-2 pr-3 pl-8 text-sm outline-none"
              />
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <label htmlFor="journeys-trigger" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Trigger</Trans>
            </label>
            <select
              id="journeys-trigger"
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              className="border-border bg-background min-h-11 w-full rounded-md border px-2 py-1.5 text-sm sm:w-auto"
            >
              <option value="">{t`All triggers`}</option>
              {triggerOptions.map((event) => {
                const label = journeyTriggerLabel(i18n, event);
                return (
                  <option key={event} value={event}>
                    {knownTrigger(event) ? label : t`${label} (never fires)`}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label htmlFor="journeys-status" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Status</Trans>
            </label>
            <select
              id="journeys-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              className="border-border bg-background min-h-11 w-full rounded-md border px-2 py-1.5 text-sm sm:w-auto"
            >
              <option value="">{t`All statuses`}</option>
              <option value="active">{t`Active`}</option>
              <option value="inactive">{t`Inactive`}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive-soft text-destructive-strong rounded-md px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Table — a row opens the drawer, which shows every column hidden here. */}
      <div className="border-border/50 overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-border/50 bg-muted/30 border-b">
              <th className="px-3 py-2 text-left font-semibold">
                <Trans>Journey</Trans>
              </th>
              <th className="hidden px-3 py-2 text-left font-semibold sm:table-cell">
                <Trans>Trigger</Trans>
              </th>
              <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
                <Trans>Steps</Trans>
              </th>
              <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
                <Trans>Enrollments</Trans>
              </th>
              <th className="px-3 py-2 text-left font-semibold">
                <Trans>Status</Trans>
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                <span className="sr-only">
                  <Trans>Actions</Trans>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-border/30 border-b last:border-0">
                  <td className="px-3 py-3" colSpan={6}>
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted-foreground p-8 text-center text-sm">
                  {journeys.length === 0 ? (
                    <Trans>
                      No journeys yet. Create the first one, or add the defaults with “Ensure
                      default journeys”.
                    </Trans>
                  ) : (
                    <Trans>No journeys match the current filter.</Trans>
                  )}
                </td>
              </tr>
            ) : (
              filtered.map((j) => {
                const code = j.code;
                const dead = !knownTrigger(j.triggerEvent);
                return (
                  <tr
                    key={j.id}
                    onClick={() => setOpenDetailId(j.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setOpenDetailId(j.id);
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={t`Open journey ${code}`}
                    className="border-border/30 hover:bg-muted/30 focus-visible:bg-muted/40 focus-visible:outline-primary cursor-pointer border-b last:border-0 focus-visible:outline-2 focus-visible:-outline-offset-2"
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{j.name}</div>
                      <div className="text-muted-foreground font-mono text-xs">{j.code}</div>
                      {j.description && (
                        <div className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                          {j.description}
                        </div>
                      )}
                    </td>
                    <td className="hidden px-3 py-2.5 sm:table-cell">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant="secondary" className="text-[0.625rem]">
                          {journeyTriggerLabel(i18n, j.triggerEvent)}
                        </Badge>
                        {dead && (
                          <Badge variant="warning" className="text-[0.625rem]">
                            <Trans>Never fires</Trans>
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-3 py-2.5 text-right font-mono md:table-cell">
                      {j._count.steps}
                    </td>
                    <td className="hidden px-3 py-2.5 text-right font-mono md:table-cell">
                      {j._count.enrollments}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge
                        variant={j.isActive ? "success" : "neutral"}
                        className="text-[0.625rem]"
                      >
                        {j.isActive ? <Trans>Active</Trans> : <Trans>Inactive</Trans>}
                      </Badge>
                    </td>
                    <td
                      className="px-3 py-2.5 text-right"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={togglingId === j.id}
                        onClick={() => void toggleActive(j)}
                        aria-label={j.isActive ? t`Pause` : t`Resume`}
                        className="min-h-11 min-w-11"
                      >
                        {j.isActive ? (
                          <PowerOff className="size-3.5" />
                        ) : (
                          <Power className="size-3.5" />
                        )}
                        <span className="hidden sm:inline">
                          {j.isActive ? <Trans>Pause</Trans> : <Trans>Resume</Trans>}
                        </span>
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <JourneyBuilderModal
        open={showBuilder}
        onOpenChange={(o) => {
          setShowBuilder(o);
          if (!o) setEditId(null);
        }}
        journeyId={editId}
        onSaved={() => {
          refresh();
          setEditId(null);
        }}
      />

      <JourneyDetailDrawer
        journeyId={openDetailId}
        open={openDetailId !== null}
        onOpenChange={(o) => {
          if (!o) setOpenDetailId(null);
        }}
        onUpdated={refresh}
        onEdit={(id) => openEdit(id)}
      />
    </div>
  );
}
